import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { GitHubIcon, LinkedInIcon, MailIcon, MediumIcon, SearchIcon } from '../common/Icons';

export interface HomeApp {
  id: string;
  name: string;
  icon?: string;
}

export interface DockLink {
  href: string;
  label: string;
  kind: 'linkedin' | 'github' | 'medium' | 'mail';
}

const Root = styled(motion.div)`
  position: absolute;
  inset: 0;
  overflow: hidden;
  transform-origin: center 45%;
`;

const Wallpaper = styled.div`
  position: absolute;
  inset: -20%;
  background:
    radial-gradient(80% 55% at 50% 108%, oklch(58% 0.15 42) 0%, transparent 72%),
    radial-gradient(70% 45% at 0% 0%, oklch(34% 0.03 250) 0%, transparent 70%),
    linear-gradient(180deg, oklch(22% 0.012 255), oklch(16% 0.01 40));
`;

const Grid = styled.div`
  position: absolute;
  top: 17cqw;
  left: 6.5cqw;
  right: 6.5cqw;
  display: grid;
  grid-template-columns: repeat(4, 15.3cqw);
  justify-content: space-between;
  row-gap: 5cqw;
`;

const AppButton = styled(motion.button)`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1.4cqw;
  border: none;
  background: none;
  padding: 0;
  cursor: pointer;
  color: #fff;
  -webkit-tap-highlight-color: transparent;
`;

export const IconTile = styled.div<{ $src?: string }>`
  position: relative;
  width: 15.3cqw;
  height: 15.3cqw;
  border-radius: 3.5cqw;
  background: ${props => (props.$src ? `#fff url(${props.$src}) center / cover no-repeat` : 'linear-gradient(135deg, #3a3a3c, #1c1c1e)')};
  box-shadow: 0 0.8cqw 2cqw rgba(0, 0, 0, 0.25);
  display: grid;
  place-items: center;
  font-size: 6cqw;
  font-weight: 700;
  color: #fff;

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: inherit;
    box-shadow: inset 0 0 0 0.25cqw rgba(255, 255, 255, 0.08);
  }
`;

const Label = styled.span`
  width: 21cqw;
  font-size: 2.85cqw;
  font-weight: 500;
  line-height: 1.2;
  text-align: center;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  text-shadow: 0 0.3cqw 1cqw rgba(0, 0, 0, 0.45);
  font-family: -apple-system, BlinkMacSystemFont, 'SF Pro Text', 'Inter', sans-serif;
`;

const SearchPill = styled(motion.div)`
  position: absolute;
  left: 50%;
  bottom: 29.5cqw;
  display: flex;
  align-items: center;
  gap: 1.2cqw;
  padding: 1.5cqw 3.6cqw;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(12px);
  color: #fff;
  font-size: 3.2cqw;
  font-weight: 500;
  font-family: -apple-system, BlinkMacSystemFont, 'SF Pro Text', 'Inter', sans-serif;

  svg {
    width: 3.2cqw;
    height: 3.2cqw;
  }
`;

const Dock = styled(motion.div)`
  position: absolute;
  left: 3cqw;
  right: 3cqw;
  bottom: 3cqw;
  height: 23.5cqw;
  border-radius: 9.5cqw;
  display: flex;
  align-items: center;
  justify-content: space-around;
  padding: 0 2.5cqw;
  background: rgba(255, 255, 255, 0.18);
  backdrop-filter: blur(24px) saturate(160%);
  -webkit-backdrop-filter: blur(24px) saturate(160%);
`;

const DockApp = styled(motion.a)<{ $bg: string }>`
  width: 15.3cqw;
  height: 15.3cqw;
  border-radius: 3.5cqw;
  display: grid;
  place-items: center;
  color: #fff;
  background: ${props => props.$bg};
  box-shadow: 0 0.8cqw 2cqw rgba(0, 0, 0, 0.25);

  svg {
    width: 7.5cqw;
    height: 7.5cqw;
  }
`;

const dockStyles: Record<DockLink['kind'], { bg: string; icon: React.ReactNode }> = {
  linkedin: { bg: 'linear-gradient(180deg, #1a8cff, #0a66c2)', icon: <LinkedInIcon /> },
  github: { bg: 'linear-gradient(180deg, #3a3a3c, #0d1117)', icon: <GitHubIcon /> },
  medium: { bg: 'linear-gradient(180deg, #2c2c2e, #000)', icon: <MediumIcon /> },
  mail: { bg: 'linear-gradient(180deg, #5ac8fa, #0a84ff)', icon: <MailIcon /> },
};

interface HomeScreenProps {
  apps: HomeApp[];
  dock: DockLink[];
  booted: boolean;
  appOpen: boolean;
  searchLabel: string;
  onLaunch: (id: string) => void;
  registerIcon: (id: string, element: HTMLDivElement | null) => void;
}

const HomeScreen: React.FC<HomeScreenProps> = ({ apps, dock, booted, appOpen, searchLabel, onLaunch, registerIcon }) => (
  <Root
    animate={{ scale: appOpen ? 1.1 : 1, opacity: appOpen ? 0.7 : 1 }}
    transition={{ type: 'spring', duration: 0.5, bounce: 0 }}
  >
    <Wallpaper />

    <Grid>
      {apps.map((app, index) => {
        const column = index % 4;
        const row = Math.floor(index / 4);
        // Icons fly in from outside the grid, like the iOS unlock animation.
        const fromX = (column - 1.5) * 14;
        const fromY = (row - 1) * 16;
        return (
          <AppButton
            key={app.id}
            onClick={() => onLaunch(app.id)}
            aria-label={app.name}
            initial={{ opacity: 0, scale: 1.25, x: `${fromX}cqw`, y: `${fromY}cqw` }}
            animate={booted ? { opacity: 1, scale: 1, x: '0cqw', y: '0cqw' } : undefined}
            transition={{ type: 'spring', duration: 0.7, bounce: 0, delay: 0.2 + (Math.abs(column - 1.5) + row) * 0.03 }}
            whileTap={{ scale: 0.94 }}
          >
            <IconTile ref={element => registerIcon(app.id, element)} $src={app.icon}>
              {!app.icon && app.name.charAt(0)}
            </IconTile>
            <Label>{app.name}</Label>
          </AppButton>
        );
      })}
    </Grid>

    <SearchPill
      style={{ x: '-50%' }}
      initial={{ opacity: 0 }}
      animate={booted ? { opacity: 1 } : undefined}
      transition={{ delay: 0.5, duration: 0.3 }}
    >
      <SearchIcon />
      {searchLabel}
    </SearchPill>

    <Dock
      initial={{ opacity: 0, y: '12cqw' }}
      animate={booted ? { opacity: 1, y: '0cqw' } : undefined}
      transition={{ type: 'spring', duration: 0.7, bounce: 0, delay: 0.3 }}
    >
      {dock.map(link => (
        <DockApp
          key={link.kind}
          href={link.href}
          target={link.kind === 'mail' ? undefined : '_blank'}
          rel="noopener noreferrer"
          aria-label={link.label}
          title={link.label}
          $bg={dockStyles[link.kind].bg}
          whileTap={{ scale: 0.94 }}
        >
          {dockStyles[link.kind].icon}
        </DockApp>
      ))}
    </Dock>
  </Root>
);

export default HomeScreen;
